import { FunctionComponent, ReactNode } from "react";
import { Button, Card, Typography } from "antd";
import { RightOutlined, RobotOutlined, SmileOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/AuthContext";
import { getNavGroups, NavItem } from "../../components/layout/navigationConfig";
import { getChatbotSuggestions } from "../../components/chatbot/chatbotSuggestions";
import { useChatbotStore } from "../../components/stores/useChatbotStore";
import { canUseChatbot } from "../../services/user";
import { UnahtorizedAccess } from "../public/UnahtorizedAccess";

interface Shortcut {
    key: string;
    label: string;
    icon?: ReactNode;
    path: string;
    state?: unknown;
}

// Aplati les items de navigation en destinations réelles (feuilles avec un `path`).
// Les enfants héritent du libellé et de l'icône de leur parent pour rester lisibles.
const collectShortcuts = (items: NavItem[], parent?: NavItem): Shortcut[] =>
    items.flatMap((item) => {
        if (item.children) {
            return collectShortcuts(item.children, item);
        }
        if (!item.path) {
            return [];
        }
        return [{
            key: item.key,
            label: parent ? `${parent.label} — ${item.label}` : item.label,
            icon: item.icon ?? parent?.icon,
            path: item.path,
            state: item.state,
        }];
    });

const getSousTitre = (roles: string[]): string => {
    if (roles.includes("ROLE_ADMIN")) return "Espace administrateur — vos accès rapides";
    if (roles.includes("ROLE_ENSEIGNANT")) return "Espace enseignant — vos accès rapides";
    if (roles.includes("ROLE_TRESORIER")) return "Espace trésorier — vos accès rapides";
    return "Vos accès rapides";
};

export const HomeAdmin: FunctionComponent = () => {
    const { username, prenom, roles } = useAuth();
    const navigate = useNavigate();
    const setChatbotOpen = useChatbotStore((state) => state.setOpen);
    const setPendingQuestion = useChatbotStore((state) => state.setPendingQuestion);

    if (!username) {
        return <UnahtorizedAccess />;
    }

    const displayName = prenom ?? username;
    const groups = getNavGroups(roles ?? []);
    const showAssistant = canUseChatbot(roles);
    const suggestions = getChatbotSuggestions(roles).slice(0, 3);

    const askAssistant = (question: string) => {
        setPendingQuestion(question);
        setChatbotOpen(true);
    };

    return (
        <div className="centered-content">
            <div className="container-full-width">
                <div className="home-admin-hero">
                    <div className="home-admin-hero-badge">
                        <SmileOutlined style={{ fontSize: 24, color: "#ffffff" }} />
                    </div>
                    <div>
                        <Typography.Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
                            Bienvenue, {displayName}
                        </Typography.Title>
                        <Typography.Text type="secondary">{getSousTitre(roles ?? [])}</Typography.Text>
                    </div>
                </div>

                {showAssistant && (
                    <div className="home-admin-assistant">
                        <span className="home-admin-assistant-icon">
                            <RobotOutlined />
                        </span>
                        <div className="home-admin-assistant-body">
                            <div className="home-admin-assistant-title">Une question sur l'utilisation de la plateforme ?</div>
                            <Typography.Text type="secondary">
                                Posez votre question, l'assistant peut peut-être vous répondre.
                            </Typography.Text>
                            {suggestions.length > 0 && (
                                <div className="home-admin-assistant-suggestions">
                                    {suggestions.map((suggestion) => (
                                        <button
                                            key={suggestion}
                                            type="button"
                                            className="home-admin-assistant-suggestion"
                                            onClick={() => askAssistant(suggestion)}
                                        >
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <Button type="primary" onClick={() => setChatbotOpen(true)}>
                            Ouvrir l'assistant
                        </Button>
                    </div>
                )}

                {groups.map((group, groupIndex) => {
                    const shortcuts = collectShortcuts(group.items);
                    if (shortcuts.length === 0) {
                        return null;
                    }
                    return (
                        <div key={group.label ?? `group-${groupIndex}`} className="home-admin-section">
                            {group.label && <div className="home-admin-group-title">{group.label}</div>}
                            <div className="home-admin-grid">
                                {shortcuts.map((shortcut) => (
                                    <Card
                                        key={shortcut.key}
                                        hoverable
                                        size="small"
                                        className="home-admin-card"
                                        onClick={() => navigate(shortcut.path, shortcut.state ? { state: shortcut.state } : undefined)}
                                    >
                                        <div className="home-admin-card-inner">
                                            <span className="home-admin-card-icon">{shortcut.icon}</span>
                                            <span className="home-admin-card-label">{shortcut.label}</span>
                                            <RightOutlined className="home-admin-card-arrow" />
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
